import { useNavigate } from 'react-router'
import type { SafetyProfile } from '@/types/profiles'
import { useSOSStore } from '@/stores/sos'
import { usePanicStore } from '@/stores/panic'
import { ROUTES } from '@/constants/routes'

interface Props {
  profile: SafetyProfile
}

export function ProfileCard({ profile }: Props) {
  const navigate = useNavigate()
  const sosProfileId = useSOSStore((s) => s.mySOSStatus?.profileId ?? null)
  const panicProfileId = usePanicStore((s) => s.myPanicStatus?.profileId ?? null)
  const sosActive = sosProfileId === profile.id || panicProfileId === profile.id

  return (
    <div className="rounded-xl border border-slate-200/70 shadow-md hover:shadow-lg overflow-hidden flex flex-col transition-all bg-white">
      {/* Clickable area → profile detail */}
      <button
        onClick={() => navigate(ROUTES.PROFILE_DETAIL.replace(':id', profile.id))}
        className="w-full p-2 sm:p-3 text-left hover:bg-beacon-50 active:bg-beacon-100 transition-colors flex-1"
      >
        {/* Avatar */}
        <div className="flex justify-center mb-1.5 sm:mb-2">
          <div className="relative inline-flex">
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border border-beacon-300 ring-2 ring-beacon-100 shadow-sm flex items-center justify-center flex-shrink-0 text-lg sm:text-xl bg-beacon-50 bg-center bg-cover"
              style={profile.photoUrl ? { backgroundImage: `url('${profile.photoUrl}')` } : undefined}
            >
              {!profile.photoUrl && <span>{profile.emoji || '🧒'}</span>}
            </div>
            {sosActive && (
              <span className="absolute bottom-0 right-0 flex">
                <span className="animate-ping absolute w-3 h-3 rounded-full bg-danger-400 opacity-75" />
                <span className="w-3 h-3 rounded-full bg-danger-500 ring-2 ring-white" />
              </span>
            )}
          </div>
        </div>

        {/* Name */}
        <p className="font-semibold text-[13px] text-slate-900 truncate text-center leading-tight">{profile.firstName}</p>
        {profile.nickname && (
          <p className="text-[11px] text-slate-500 font-medium text-center truncate mt-0.5">{profile.nickname}</p>
        )}

        {/* Pills */}
        <div className="mt-1 sm:mt-2 flex flex-wrap items-center gap-1 justify-center">
          {!!profile.age && (
            <span className="text-[11px] font-semibold text-success-700 bg-white/60 border border-success-200 px-1.5 py-0.5 rounded-full">
              {profile.age} yrs
            </span>
          )}
          {profile.bloodType && (
            <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full border border-danger-200 bg-white/60 text-danger-700">
              🩸 {profile.bloodType}
            </span>
          )}
          {(profile.conditions ?? []).slice(0, 1).map((c) => (
            <span key={c} className="text-[11px] font-semibold px-1.5 py-0.5 rounded-full hidden sm:inline-flex border border-slate-200 bg-white/60 text-slate-600">
              {c}
            </span>
          ))}
        </div>
      </button>

      {/* Quick actions */}
      <div className="bg-beacon-50 border-t border-beacon-100 px-2 py-1.5 sm:py-2 flex items-center justify-center gap-1.5">
        <button
          onClick={() => navigate(sosActive ? ROUTES.MISSION_CONTROL : `${ROUTES.SOS}?profile=${profile.id}`)}
          className={[
            'flex-1 flex items-center justify-center gap-1 text-xs font-semibold min-h-10 rounded-xl border active:scale-95 transition-all',
            sosActive
              ? 'border-danger-500 bg-danger-500 text-white animate-pulse hover:bg-danger-600'
              : 'border-danger-200 bg-danger-50 text-danger-600 hover:bg-danger-100',
          ].join(' ')}
        >
          <i className="fa-solid fa-triangle-exclamation text-xs" />
          <span className="hidden sm:inline">SOS</span>
        </button>
        <button
          onClick={() => navigate(`${ROUTES.LOCATION}?profile=${profile.id}`)}
          className="flex-1 flex items-center justify-center gap-1 text-xs font-semibold py-1.5 rounded-xl border border-success-200 bg-success-50 text-success-700 hover:bg-success-100 active:scale-95 transition-all"
        >
          <i className="fa-solid fa-location-dot text-xs" />
          <span className="hidden sm:inline">Track</span>
        </button>
        <button
          onClick={() => navigate(`${ROUTES.QR}?profileId=${profile.id}`)}
          className="flex-1 flex items-center justify-center gap-1 text-xs font-semibold py-1.5 rounded-xl border border-beacon-200 bg-beacon-50 text-beacon-700 hover:bg-beacon-100 active:scale-95 transition-all"
        >
          <i className="fa-solid fa-qrcode text-xs" />
          <span className="hidden sm:inline">QR</span>
        </button>
        <button
          onClick={() => navigate(`/aac/${profile.id}`)}
          className="flex-1 flex items-center justify-center gap-1 text-xs font-semibold py-1.5 rounded-xl border border-community-200 bg-community-50 text-community-700 hover:bg-community-100 active:scale-95 transition-all"
        >
          <i className="fa-solid fa-comments text-xs" />
          <span className="hidden sm:inline">Voice</span>
        </button>
      </div>
    </div>
  )
}
